import { RefreshCw, X } from "lucide-react";
import React, { useRef } from "react";

import * as api from "@/api/supply";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { useDialogFocus } from "../ozon-analytics/useDialogFocus";
import { dec1, fmtDate, fmtDateTime, int0 } from "./format";
import { useWbCompare } from "./useWbSource";

const FIELD_LABEL: Record<api.WbCompareField, string> = {
  orders_7d_qty: "Заказы 7д",
  orders_qty: "Заказы 30д",
  selling_speed: "Скорость, шт/дн",
  stock_qty: "Остаток FBO",
  supply_qty: "В пути",
  stock_need: "Потребность",
  supply_result: "Допоставить",
};

const SIDE_LABEL: Record<api.WbSource, string> = {
  legacy: "старый",
  mart: "витрина",
};

const num = (f: api.WbCompareField, v: number): string =>
  f === "selling_speed" ? dec1(v) : int0(v);

function deltaText(f: api.WbCompareField, v: number): string {
  if (v === 0) return "0";
  return (v > 0 ? "+" : "−") + num(f, Math.abs(v));
}

/** Три ячейки поля: старый / витрина / Δ (= витрина − старый). */
function Triple({
  f,
  n,
}: {
  f: api.WbCompareField;
  n: api.WbCompareNums;
}): React.ReactElement {
  const d = n[`${f}_delta`];
  return (
    <>
      <td className="border-l px-1.5 py-1 text-right tabular-nums">
        {num(f, n[`${f}_legacy`])}
      </td>
      <td className="px-1.5 py-1 text-right tabular-nums">
        {num(f, n[`${f}_mart`])}
      </td>
      <td
        className={cn(
          "px-1.5 py-1 text-right tabular-nums",
          d > 0 && "font-medium text-sky-700 dark:text-sky-400",
          d < 0 && "font-medium text-amber-700 dark:text-amber-400",
          d === 0 && "text-muted-foreground/60",
        )}
      >
        {deltaText(f, d)}
      </td>
    </>
  );
}

function FieldHeader({ lead }: { lead: string[] }): React.ReactElement {
  return (
    <thead className="sticky top-0 bg-background text-xs text-muted-foreground">
      <tr>
        {lead.map((h) => (
          <th key={h} rowSpan={2} className="px-1.5 py-1 text-left font-medium">
            {h}
          </th>
        ))}
        {api.WB_COMPARE_FIELDS.map((f) => (
          <th
            key={f}
            colSpan={3}
            className="border-l px-1.5 py-1 text-center font-medium"
          >
            {FIELD_LABEL[f]}
          </th>
        ))}
      </tr>
      <tr className="border-b">
        {api.WB_COMPARE_FIELDS.map((f) => (
          <React.Fragment key={f}>
            <th className="border-l px-1.5 py-0.5 text-right font-normal">
              старый
            </th>
            <th className="px-1.5 py-0.5 text-right font-normal">витрина</th>
            <th className="px-1.5 py-0.5 text-right font-normal">Δ</th>
          </React.Fragment>
        ))}
      </tr>
    </thead>
  );
}

function sumRegions(regions: api.WbCompareRegion[]): api.WbCompareNums {
  const out = {} as api.WbCompareNums;
  for (const f of api.WB_COMPARE_FIELDS)
    for (const s of ["legacy", "mart", "delta"] as const) {
      const k = `${f}_${s}` as const;
      out[k] = regions.reduce((acc, r) => acc + r[k], 0);
    }
  return out;
}

function Side({
  label,
  side,
}: {
  label: string;
  side: api.WbCompareSide;
}): React.ReactElement {
  return (
    <div className="space-y-0.5">
      <div>
        <b>{label}:</b> остаток на {fmtDate(side.as_of)} · заказы{" "}
        {fmtDate(side.orders_from)} — {fmtDate(side.orders_to)}
        {side.mart_built_at && <> · сборка {fmtDateTime(side.mart_built_at)}</>}
        {" · "}строк {int0(side.rows_count)}
      </div>
      {side.warnings.map((w, i) => (
        <div key={i} className="text-amber-700 dark:text-amber-400">
          • {w}
        </div>
      ))}
    </div>
  );
}

/** Сравнение источников WB «Распределения»: те же отсечки и запреты, что на странице; по регионам
 *  и топ расхождений товар × регион по «Допоставить». */
export function WbSourceCompare({
  params,
  onClose,
}: {
  params: api.CalcParams;
  onClose: () => void;
}): React.ReactElement {
  const q = useWbCompare(params, true);
  const d = q.data;
  const onlyIn = (s: api.WbSource): number =>
    d ? d.rows.filter((r) => r.only_in === s).length : 0;
  const total = d ? sumRegions(d.regions) : null;
  // при открытии фокус — на «Закрыть», при закрытии — обратно на кнопку сравнения; Esc закрывает,
  // Tab не уходит под подложку
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useDialogFocus(dialogRef, closeRef, onClose);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/60"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Сравнение источников WB"
        className="max-h-[90vh] w-full max-w-[95vw] overflow-auto rounded-lg border bg-background p-4 text-sm shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold">
            Сравнение источников WB: старый (сырые таблицы) × витрина dwh
            {d && <> на {fmtDate(d.as_of)}</>}
          </h3>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            className="rounded p-1 hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Отсечки ваши (ГТЗ {params.target}, потолок {params.overstock}, порог{" "}
          {params.catalog} дн). Δ = витрина − старый. Скорость региона — сумма
          скоростей строк.
        </p>

        {q.isLoading && (
          <p className="flex items-center gap-2 text-muted-foreground">
            <RefreshCw className="size-4 animate-spin" /> Считаю оба источника…
          </p>
        )}
        {q.error ? (
          <p className="text-destructive">
            {extractErrorMessage(q.error, "Сравнение не получено")}
          </p>
        ) : null}

        {d && (
          <div className="space-y-4">
            <div className="space-y-1 text-xs">
              <Side label="Старый" side={d.legacy} />
              <Side label="Витрина" side={d.mart} />
              {d.warnings.map((w, i) => (
                <div key={i} className="text-amber-700 dark:text-amber-400">
                  • {w}
                </div>
              ))}
            </div>

            <section>
              <h4 className="mb-1 font-medium">По регионам</h4>
              <div className="overflow-x-auto rounded border">
                <table className="w-full text-xs">
                  <FieldHeader lead={["Регион"]} />
                  <tbody>
                    {d.regions.map((r) => (
                      <tr key={r.region} className="border-b">
                        <td className="whitespace-nowrap px-1.5 py-1">
                          {r.region}
                        </td>
                        {api.WB_COMPARE_FIELDS.map((f) => (
                          <Triple key={f} f={f} n={r} />
                        ))}
                      </tr>
                    ))}
                    {total && (
                      <tr className="bg-muted/40 font-medium">
                        <td className="px-1.5 py-1">Итого</td>
                        {api.WB_COMPARE_FIELDS.map((f) => (
                          <Triple key={f} f={f} n={total} />
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h4 className="mb-1 font-medium">
                Расхождения «Допоставить» товар × регион
              </h4>
              <p className="mb-1 text-xs text-muted-foreground">
                сравнено строк <b>{int0(d.rows_compared)}</b> · с Δ
                «Допоставить»{" "}
                <b data-testid="wb-compare-rows-diff">{int0(d.rows_diff)}</b> ·
                показано {int0(d.rows.length)} (топ {d.top} по |Δ|) · из них
                только в старом <b>{onlyIn("legacy")}</b>, только в витрине{" "}
                <b>{onlyIn("mart")}</b>
              </p>
              {d.rows.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  «Допоставить» не расходится ни в одной строке.
                </p>
              ) : (
                <div className="overflow-x-auto rounded border">
                  <table className="w-full text-xs">
                    <FieldHeader
                      lead={["Регион", "Артикул", "Товар", "nm", "Есть"]}
                    />
                    <tbody>
                      {d.rows.map((r) => (
                        <tr
                          key={`${r.region}\u0000${r.mp_sku}`}
                          className="border-b"
                        >
                          <td className="whitespace-nowrap px-1.5 py-1">
                            {r.region}
                          </td>
                          <td className="whitespace-nowrap px-1.5 py-1">
                            {r.article || r.mp_sku}
                          </td>
                          <td
                            className="max-w-64 truncate px-1.5 py-1"
                            title={r.nom_name}
                          >
                            {r.nom_name || "—"}
                          </td>
                          <td className="px-1.5 py-1 tabular-nums">
                            {r.mp_sku}
                          </td>
                          <td className="whitespace-nowrap px-1.5 py-1">
                            {r.only_in
                              ? `только ${SIDE_LABEL[r.only_in]}`
                              : "оба"}
                          </td>
                          {api.WB_COMPARE_FIELDS.map((f) => (
                            <Triple key={f} f={f} n={r} />
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="text-xs">
              <h4 className="mb-1 text-sm font-medium">
                Не сопоставлено в старом источнике (→ «Не известен»)
              </h4>
              <div>
                Склады:{" "}
                {d.unmatched_warehouses.length
                  ? d.unmatched_warehouses.join(", ")
                  : "нет"}
              </div>
              <div>
                Округа:{" "}
                {d.unmatched_okrugs.length
                  ? d.unmatched_okrugs.join(", ")
                  : "нет"}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
